import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSEO, { trackEvent } from '@/hooks/useSEO';
import { ARTICLES, getArticleBySlug, formatPublishDate } from '@/blog/articles';

const STYLES = `
.blog-art{background:#FAF7F0;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;color:#0D1B2A;}
.blog-art-nav{background:#fff;border-bottom:1px solid rgba(200,168,75,.2);padding:22px 48px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:50;backdrop-filter:blur(20px);background:rgba(255,255,255,.94);}
.blog-art-back{background:none;border:none;font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#3A4A5C;cursor:pointer;}
.blog-art-back:hover{color:#C8A84B;}
.blog-art-logo{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;letter-spacing:.04em;color:#C8A84B;}
.blog-art-logo em{font-style:italic;font-weight:300;color:#0D1B2A;}

.blog-art-hero{padding:80px 48px 56px;max-width:820px;margin:0 auto;}
.blog-breadcrumb{font-size:11px;font-weight:500;letter-spacing:.08em;color:#7A8190;margin-bottom:20px;}
.blog-breadcrumb a{color:#7A8190;text-decoration:none;cursor:pointer;}
.blog-breadcrumb a:hover{color:#C8A84B;}
.blog-breadcrumb .sep{margin:0 8px;color:#C8A84B;}
.blog-art-cat{display:inline-block;font-size:9px;font-weight:800;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;background:rgba(200,168,75,.08);border:1px solid rgba(200,168,75,.25);padding:6px 14px;border-radius:50px;margin-bottom:22px;}
.blog-art-h1{font-family:'Cormorant Display',serif;font-size:clamp(42px,5.5vw,72px);font-weight:700;line-height:1;letter-spacing:-.022em;margin:0 0 24px;}
.blog-art-h1 em{font-style:italic;color:#C8A84B;}
.blog-art-excerpt{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;font-size:clamp(18px,2vw,22px);color:#3A4A5C;line-height:1.55;margin:0 0 36px;}
.blog-art-author{display:flex;align-items:center;gap:16px;padding:20px 0;border-top:1px solid rgba(200,168,75,.18);border-bottom:1px solid rgba(200,168,75,.18);}
.blog-art-author-avatar{width:52px;height:52px;border-radius:50%;background:#C8A84B;color:#0D1B2A;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Display',serif;font-style:italic;font-weight:700;font-size:20px;background-size:cover;background-position:center 15%;cursor:pointer;}
.blog-art-author-meta{flex:1;}
.blog-art-author-name{font-size:14px;font-weight:700;color:#0D1B2A;cursor:pointer;}
.blog-art-author-name:hover{color:#C8A84B;}
.blog-art-author-role{font-size:12px;color:#7A8190;margin-top:2px;}
.blog-art-meta-r{display:flex;gap:16px;align-items:center;}
.blog-art-meta-r-cell{font-size:11px;letter-spacing:.04em;color:#7A8190;display:flex;flex-direction:column;gap:2px;}
.blog-art-meta-r-cell strong{color:#0D1B2A;font-weight:700;font-size:12px;}

/* Body */
.blog-art-body{max-width:760px;margin:0 auto;padding:48px 48px 80px;font-size:17px;line-height:1.85;color:#1F2D3D;font-weight:400;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;}
.blog-art-body p{margin:0 0 24px;}
.blog-art-body h2{font-family:'Cormorant Display',serif;font-size:clamp(30px,3.4vw,42px);font-weight:700;line-height:1.15;letter-spacing:-.015em;margin:48px 0 20px;color:#0D1B2A;}
.blog-art-body h2::before{content:'';display:block;width:40px;height:2px;background:#C8A84B;margin-bottom:16px;}
.blog-art-body h3{font-family:'Cormorant Display',serif;font-size:clamp(22px,2.4vw,28px);font-weight:700;font-style:italic;color:#0D1B2A;margin:32px 0 14px;letter-spacing:-.005em;}
.blog-art-body ul,.blog-art-body ol{margin:0 0 28px;padding-left:0;list-style:none;}
.blog-art-body ul li,.blog-art-body ol li{padding:10px 0 10px 32px;border-bottom:1px solid rgba(13,27,42,.06);position:relative;line-height:1.7;}
.blog-art-body ul li::before{content:'';position:absolute;left:4px;top:18px;width:6px;height:6px;background:#C8A84B;transform:rotate(45deg);}
.blog-art-body ol{counter-reset:numlist;}
.blog-art-body ol li{counter-increment:numlist;}
.blog-art-body ol li::before{content:counter(numlist, decimal-leading-zero);position:absolute;left:0;top:10px;font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-weight:700;font-size:14px;}
.blog-art-body ul li:last-child,.blog-art-body ol li:last-child{border-bottom:none;}

.blog-art-callout{margin:48px 0;padding:32px 36px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid rgba(200,168,75,.3);position:relative;overflow:hidden;}
.blog-art-callout::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;background:#C8A84B;}
.blog-art-callout-title{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#C8A84B;line-height:1.15;margin-bottom:12px;letter-spacing:-.01em;}
.blog-art-callout-text{font-size:15px;line-height:1.7;color:rgba(250,247,240,.9);margin-bottom:20px;font-weight:300;}
.blog-art-callout-cta{display:inline-flex;align-items:center;gap:8px;font-size:10px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;color:#0D1B2A;background:#C8A84B;padding:13px 26px;border-radius:50px;border:none;cursor:pointer;transition:background .2s;}
.blog-art-callout-cta:hover{background:#FAF7F0;}

.blog-art-disclaimer{margin:56px 0 0;padding:20px 24px;background:rgba(200,168,75,.05);border:1px solid rgba(200,168,75,.18);border-left:3px solid #C8A84B;font-size:12.5px;line-height:1.7;color:#3A4A5C;font-style:italic;font-weight:400;}

/* Share row */
.blog-art-share{max-width:760px;margin:0 auto;padding:0 48px 40px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;border-top:1px solid rgba(200,168,75,.18);padding-top:28px;}
.blog-art-share-l{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#7A8190;margin-right:8px;}
.blog-art-share-btn{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:9px 16px;border-radius:50px;border:1px solid rgba(200,168,75,.3);background:transparent;color:#0D1B2A;cursor:pointer;text-decoration:none;transition:all .2s;}
.blog-art-share-btn:hover{background:#C8A84B;border-color:#C8A84B;color:#0D1B2A;}
.blog-art-share-btn.gold{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.blog-art-share-btn.gold:hover{background:#0D1B2A;color:#C8A84B;border-color:#0D1B2A;}

/* LinkedIn composer modal */
.blog-li-back{position:fixed;inset:0;background:rgba(13,27,42,.85);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:32px;animation:liIn .25s ease-out;}
@keyframes liIn{from{opacity:0;}to{opacity:1;}}
.blog-li-modal{width:100%;max-width:640px;background:#fff;border:1px solid rgba(200,168,75,.35);border-left:3px solid #C8A84B;display:flex;flex-direction:column;overflow:hidden;max-height:calc(100vh - 64px);}
.blog-li-head{display:flex;justify-content:space-between;align-items:center;padding:20px 26px;background:#FAF7F0;border-bottom:1px solid rgba(200,168,75,.18);}
.blog-li-head-l{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;}
.blog-li-head-l em{color:#C8A84B;font-style:italic;}
.blog-li-close{background:transparent;border:1px solid rgba(13,27,42,.2);color:#0D1B2A;width:30px;height:30px;display:flex;align-items:center;justify-content:center;border-radius:50%;cursor:pointer;font-size:16px;line-height:1;}
.blog-li-close:hover{background:#0D1B2A;color:#FAF7F0;border-color:#0D1B2A;}
.blog-li-body{padding:24px 26px;overflow-y:auto;}
.blog-li-eyebrow{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#7A8190;margin-bottom:8px;}
.blog-li-textarea{width:100%;min-height:240px;border:1px solid rgba(13,27,42,.2);background:#FAF7F0;font-family:Inter,system-ui,sans-serif;font-size:14px;line-height:1.65;color:#0D1B2A;padding:14px 16px;resize:vertical;outline:none;transition:border-color .2s;}
.blog-li-textarea:focus{border-color:#C8A84B;background:#fff;}
.blog-li-foot{padding:18px 26px;background:#FAF7F0;border-top:1px solid rgba(200,168,75,.18);display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end;}
.blog-li-cta{font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:11px 22px;border:1px solid #0D1B2A;background:#0D1B2A;color:#C8A84B;cursor:pointer;border-radius:50px;transition:all .2s;text-decoration:none;display:inline-flex;align-items:center;gap:6px;}
.blog-li-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.blog-li-cta.line{background:transparent;color:#0D1B2A;border-color:rgba(13,27,42,.25);}
.blog-li-cta.line:hover{border-color:#C8A84B;color:#C8A84B;background:transparent;}
.blog-li-tip{font-size:11px;color:#3A4A5C;line-height:1.5;margin-top:10px;}
.blog-li-tip strong{color:#0D1B2A;}

/* Related */
.blog-art-related{background:#fff;border-top:1px solid rgba(200,168,75,.18);padding:72px 48px 96px;}
.blog-art-related-inner{max-width:1100px;margin:0 auto;}
.blog-art-related h3{font-family:'Cormorant Display',serif;font-size:32px;font-weight:700;margin:0 0 32px;letter-spacing:-.01em;}
.blog-art-related h3 em{font-style:italic;color:#C8A84B;}
.blog-art-related-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;}
.blog-art-related-card{background:#FAF7F0;border:1px solid rgba(200,168,75,.18);padding:24px;cursor:pointer;transition:all .25s;display:flex;flex-direction:column;gap:10px;}
.blog-art-related-card:hover{border-color:#C8A84B;background:#fff;transform:translateY(-2px);}
.blog-art-related-cat{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
.blog-art-related-t{font-family:'Cormorant Display',serif;font-size:20px;font-weight:700;line-height:1.2;color:#0D1B2A;letter-spacing:-.005em;}
.blog-art-related-m{font-size:11px;color:#7A8190;margin-top:auto;padding-top:8px;}

@media(max-width:900px){
  .blog-art-hero,.blog-art-body,.blog-art-share,.blog-art-related{padding-left:24px;padding-right:24px;}
  .blog-art-nav{padding:18px 24px;}
  .blog-art-related-grid{grid-template-columns:1fr;}
  .blog-art-meta-r{display:none;}
}
`;

const initials = (name) => name.split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase();

function renderBlock(block, i, onCta) {
  switch (block.type) {
    case 'p': return <p key={i}>{block.text}</p>;
    case 'h2': return <h2 key={i}>{block.text}</h2>;
    case 'h3': return <h3 key={i}>{block.text}</h3>;
    case 'ul': return <ul key={i}>{block.items.map((it, k) => <li key={k}>{it}</li>)}</ul>;
    case 'ol': return <ol key={i}>{block.items.map((it, k) => <li key={k}>{it}</li>)}</ol>;
    case 'callout': return (
      <div key={i} className="blog-art-callout" data-testid={`blog-callout-${i}`}>
        <div className="blog-art-callout-title">{block.title}</div>
        <div className="blog-art-callout-text">{block.text}</div>
        {block.ctaLabel && (
          <button className="blog-art-callout-cta" onClick={() => onCta(block)} data-testid={`blog-callout-cta-${i}`}>
            {block.ctaLabel}
          </button>
        )}
      </div>
    );
    case 'disclaimer': return <div key={i} className="blog-art-disclaimer">{block.text}</div>;
    default: return null;
  }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);

  const shareUrl = useMemo(() => `https://skiaxis.com/blog/${slug}`, [slug]);
  const related = useMemo(() => ARTICLES.filter(a => a.slug !== slug).slice(0, 3), [slug]);

  // LinkedIn composer state — declared BEFORE any conditional return (rules of hooks)
  const [liOpen, setLiOpen] = useState(false);
  const [liPost, setLiPost] = useState('');
  const defaultLiPost = useMemo(() => {
    if (!article) return '';
    const tagsByCategory = {
      Investment: '#InvestUAE #UAEInvestment #DubaiInvestment #FamilyOffice #SKIAxis',
      Accelerator: '#StartupAccelerator #Governance #UAE #FounderJourney #SKIAxis',
      'Free Zones': '#UAEFreeZone #IFZA #DMCC #DIFC #DubaiBusiness #SKIAxis',
      Residency: '#GoldenVisa #UAEResidency #DubaiLiving #InvestorVisa #SKIAxis',
    };
    const hashtags = tagsByCategory[article.category] || '#SKIAxis #UAE #Insights';
    return [
      article.title,
      '',
      article.excerpt,
      '',
      'Just published on SKI AXIS — a deep-dive for anyone looking at the UAE in 2026.',
      '',
      `Read it here → ${shareUrl}`,
      '',
      hashtags,
    ].join('\n');
  }, [article, shareUrl]);

  useSEO({
    title: article ? `${article.title} · SKI AXIS Blog` : 'Article · SKI AXIS Blog',
    description: article?.excerpt,
    keywords: article?.keywords,
    canonical: `/blog/${slug}`,
    ogType: 'article',
    ogImage: article?.coverImage || '/og-default.png',
    jsonLd: article ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "image": `https://skiaxis.com${article.coverImage || '/og-default.png'}`,
      "datePublished": article.publishedAt,
      "dateModified": article.publishedAt,
      "author": {
        "@type": "Person",
        "name": article.author.name,
        "jobTitle": article.author.role,
        "url": article.author.slug ? `https://skiaxis.com/forum/speakers/${article.author.slug}` : undefined,
      },
      "publisher": {
        "@type": "Organization",
        "name": "SKI AXIS",
        "logo": { "@type": "ImageObject", "url": "https://skiaxis.com/logo512.png" },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": shareUrl },
      "keywords": article.keywords,
      "articleSection": article.category,
    } : undefined,
  });

  if (!article) {
    return (
      <div className="blog-art" data-testid="blog-article-404">
        <style>{STYLES}</style>
        <nav className="blog-art-nav">
          <button className="blog-art-back" onClick={() => navigate('/blog')}>← All Articles</button>
          <div className="blog-art-logo">SKI <em>AXIS</em></div>
        </nav>
        <div style={{ textAlign: 'center', padding: '120px 24px' }}>
          <h1 style={{ fontFamily: "'Cormorant Display',serif", fontSize: 48 }}>Article not found.</h1>
          <button onClick={() => navigate('/blog')} style={{ marginTop: 24, fontSize: 11, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', background: '#C8A84B', color: '#0D1B2A', border: 'none', padding: '14px 28px', borderRadius: 50, cursor: 'pointer' }}>View All Articles →</button>
        </div>
      </div>
    );
  }

  const onCta = (block) => {
    trackEvent('blog_cta_click', { article: article.slug, cta: block.ctaLabel });
    if (block.ctaHref?.startsWith('http')) window.open(block.ctaHref, '_blank', 'noopener');
    else if (block.ctaHref) navigate(block.ctaHref);
  };

  const shareText = `${article.title} — via SKI AXIS`;

  const openLiComposer = () => {
    setLiPost(defaultLiPost);
    setLiOpen(true);
    trackEvent('blog_linkedin_composer_open', { slug: article.slug });
  };

  const copyLiPost = async () => {
    try { await navigator.clipboard.writeText(liPost); trackEvent('blog_linkedin_copy', { slug: article.slug }); } catch { /* noop */ }
  };

  const openLinkedInWithPost = () => {
    // LinkedIn share dialog only takes a URL; the user pastes the copied text.
    // We auto-copy first, then open the share dialog so they can paste immediately.
    copyLiPost();
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener');
    trackEvent('blog_linkedin_share', { slug: article.slug });
  };

  return (
    <div className="blog-art" data-testid={`blog-article-${article.slug}`}>
      <style>{STYLES}</style>
      <nav className="blog-art-nav">
        <button className="blog-art-back" onClick={() => navigate('/blog')} data-testid="blog-art-back">← All Articles</button>
        <div className="blog-art-logo">SKI <em>AXIS</em></div>
      </nav>
      <header className="blog-art-hero">
        <div className="blog-breadcrumb">
          <a onClick={() => navigate('/')}>SKI AXIS</a><span className="sep">/</span>
          <a onClick={() => navigate('/blog')}>Blog</a><span className="sep">/</span>
          <span>{article.category}</span>
        </div>
        <span className="blog-art-cat">{article.category}</span>
        <h1 className="blog-art-h1" data-testid="blog-art-title">{article.title}</h1>
        <p className="blog-art-excerpt">{article.excerpt}</p>
        <div className="blog-art-author">
          <div
            className="blog-art-author-avatar"
            style={article.author.photo ? { backgroundImage: `url(${article.author.photo})`, color: 'transparent' } : {}}
            onClick={() => article.author.slug && navigate(`/forum/speakers/${article.author.slug}`)}
          >{initials(article.author.name)}</div>
          <div className="blog-art-author-meta">
            <div className="blog-art-author-name" onClick={() => article.author.slug && navigate(`/forum/speakers/${article.author.slug}`)}>{article.author.name}</div>
            <div className="blog-art-author-role">{article.author.role}</div>
          </div>
          <div className="blog-art-meta-r">
            <div className="blog-art-meta-r-cell"><strong>{formatPublishDate(article.publishedAt)}</strong><span>Published</span></div>
            <div className="blog-art-meta-r-cell"><strong>{article.readTimeMin} min</strong><span>Read time</span></div>
          </div>
        </div>
      </header>
      <article className="blog-art-body" data-testid="blog-art-body">
        {article.body.map((b, i) => renderBlock(b, i, onCta))}
      </article>
      <div className="blog-art-share" data-testid="blog-art-share">
        <span className="blog-art-share-l">Share this article</span>
        <button type="button" className="blog-art-share-btn gold" onClick={openLiComposer} data-testid="blog-share-li-composer">✨ LinkedIn Post</button>
        <a className="blog-art-share-btn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-linkedin">LinkedIn</a>
        <a className="blog-art-share-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-whatsapp">WhatsApp</a>
        <a className="blog-art-share-btn" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-x">X</a>
        <a className="blog-art-share-btn" href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(article.excerpt + '\n\n' + shareUrl)}`} data-testid="blog-share-email">Email</a>
        <button type="button" className="blog-art-share-btn" onClick={() => { navigator.clipboard?.writeText(shareUrl); trackEvent('blog_share_copy', { slug: article.slug }); }} data-testid="blog-share-copy">Copy Link</button>
      </div>
      {related.length > 0 && (
        <section className="blog-art-related">
          <div className="blog-art-related-inner">
            <h3>Keep <em>reading</em></h3>
            <div className="blog-art-related-grid">
              {related.map((r) => (
                <div
                  key={r.slug}
                  className="blog-art-related-card"
                  onClick={() => navigate(`/blog/${r.slug}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/blog/${r.slug}`); } }}
                  data-testid={`blog-related-${r.slug}`}
                >
                  <div className="blog-art-related-cat">{r.category}</div>
                  <div className="blog-art-related-t">{r.title}</div>
                  <div className="blog-art-related-m">{formatPublishDate(r.publishedAt)} · {r.readTimeMin} min read</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LinkedIn Composer Modal */}
      {liOpen && (
        <div className="blog-li-back" onClick={() => setLiOpen(false)} data-testid="blog-li-modal">
          <div className="blog-li-modal" onClick={(e) => e.stopPropagation()}>
            <div className="blog-li-head">
              <div className="blog-li-head-l">Generate <em>LinkedIn Post</em></div>
              <button className="blog-li-close" onClick={() => setLiOpen(false)} aria-label="Close" data-testid="blog-li-close">×</button>
            </div>
            <div className="blog-li-body">
              <div className="blog-li-eyebrow">Edit, then post · {liPost.length} characters</div>
              <textarea
                className="blog-li-textarea"
                value={liPost}
                onChange={(e) => setLiPost(e.target.value)}
                data-testid="blog-li-textarea"
                spellCheck
              />
              <p className="blog-li-tip">
                <strong>How to post:</strong> Click "Copy & Open LinkedIn" → LinkedIn opens in a new tab → paste (⌘/Ctrl+V) into the post composer → publish.
                You'll get the article preview card automatically.
              </p>
            </div>
            <div className="blog-li-foot">
              <button type="button" className="blog-li-cta line" onClick={copyLiPost} data-testid="blog-li-copy">Copy text</button>
              <button type="button" className="blog-li-cta" onClick={openLinkedInWithPost} data-testid="blog-li-open">Copy &amp; Open LinkedIn →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
